/**
 * Notification badge: new-since-seen, not total open count.
 *
 * Rule:
 * - Each item has stable `id` + `stateToken` (urgency fingerprint).
 * - Opening the menu writes seen[id] = stateToken for every current item.
 * - Badge = count of items where seen[id] !== stateToken.
 * - Same entity with a new urgency (invoice newly overdue → id set changes;
 *   renewal crosses from in_lead → expired) gets a new stateToken and
 *   re-badges even if previously seen.
 */
import { getMeta, setMeta } from '../db/jobsDb.js';
import type { NotificationItem, SeenMap } from './types.js';
import {
  markNotificationsSeen,
  unseenNotificationCount,
} from './logic.js';

export const NOTIF_SEEN_META = 'notif_seen_v1';

export async function loadSeenMap(): Promise<SeenMap> {
  const raw = await getMeta(NOTIF_SEEN_META);
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    const out: SeenMap = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof v === 'string') out[k] = v;
    }
    return out;
  } catch {
    return {};
  }
}

export async function saveSeenMap(seen: SeenMap): Promise<void> {
  await setMeta(NOTIF_SEEN_META, JSON.stringify(seen));
}

export async function badgeCount(
  items: NotificationItem[],
): Promise<number> {
  const seen = await loadSeenMap();
  return unseenNotificationCount(items, seen);
}

export async function markMenuOpened(
  items: NotificationItem[],
): Promise<void> {
  const prev = await loadSeenMap();
  await saveSeenMap(markNotificationsSeen(items, prev));
}

export {
  markNotificationsSeen,
  unseenNotificationCount,
};
